import * as React from "react";
import * as H from "history";
import { toast } from "react-toastify";
import { CellInfo } from "react-table";
import * as _ from "lodash";

import { ITerminology } from "src/library/utils/string-manipulations/tokenize-string";
import {
  ITableInstance,
  BulkSelectState
} from "src/library/components/Tables/Table";
import { Table } from "src/library/components/Tables";
import { ActionButton } from "src/library/components/Button";
import { IAction } from "src/library/components/Button/ActionButton";
import { ISupportTicket } from "src/modules/support/api/support.api";
import {
  FilterParams,
  PaginatedParams
} from "src/core/shared/api";
import { IProcessJob } from "src/core/process-job/api/processJobs.api";
import useGlobalSettings from "src/core/shared/hooks/useGlobalSettings";

import { useConnectorApi } from "../api/connector.api";
import { connectorOrderImportIssueTableColumns } from "../tables/connectorOrderImportIssueTableColumns";

interface ViewAdminSupportTicketsTabProps {
  history: H.History;
  terminology: ITerminology;
}

const defaultPagination: PaginatedParams<IProcessJob> = {
  page: 0,
  limit: 20,
  offset: 0,
  sort: [ [ "startedAt", "DESC" ] ]
};

/**
 * Tab for everything related to api order imports
 */
const APIOrderImportTab: React.ReactFCWithChildren<ViewAdminSupportTicketsTabProps> = ({ history, terminology }) => {
  const TableRef = React.useRef<ITableInstance<ISupportTicket>>(null);
  const [ listOrdersWithIssuesRes, listOrdersWithIssuesReq ] = useConnectorApi("LIST_ORDER_IMPORT_ISSUES");
  const [ retryAPIJobRes, retryAPIJobReq ] = useConnectorApi("RETRY_API_JOB");
  const [ bulkHandleAPIIssueRes, bulkHandleAPIIssueReq ] = useConnectorApi("BULK_HANDLE_API_ISSUE");
  const [ { globalSettings } ] = useGlobalSettings();

  /**
   * Table actions
   */
  const tableActions = React.useCallback((row: CellInfo) => {
    const actions: IAction[] = [
      {
        title: "Retry API import",
        action: () => retryAPIJobReq({ processJobId: row.original.id })
      },
      {
        title: `View ${terminology.order.singular.text}`,
        action: () => history.push(`/orders/${row.original.order?.id}`),
        newTabPath: `/orders/${row.original.order?.id}`,
        disabled: row.original.order ? false : true
      }
    ];

    return actions;
  }, [
    history,
    retryAPIJobReq,
    terminology.order.singular.text
  ]);

  React.useEffect(() => {
    if (retryAPIJobRes.data && retryAPIJobRes.data.status === "success") {
      toast.success("Order(s) queued for retry!");

      if (TableRef.current) {
        TableRef.current.reloadTable();
        TableRef.current.resetPagination();
      }
    } else if (retryAPIJobRes.error) {
      if (TableRef.current) {
        TableRef.current.reloadTable();
      }

      toast.error(retryAPIJobRes.error.message);
    }
  }, [ retryAPIJobRes.data, retryAPIJobRes.error ]);

  // handle the bulk action retry response
  React.useEffect(() => {
    if (bulkHandleAPIIssueRes.data) {
      if (bulkHandleAPIIssueRes.data.failed === 0) {
        toast.success("Order(s) queued for retry!");
      } else {
        if (bulkHandleAPIIssueRes.data.queued === 0) {
          toast.error("None of the selected orders could be retried");
        } else {
          toast.warn("Some of the selected jobs failed to re-run");
        }
      }

      if (TableRef.current) {
        TableRef.current.reloadTable();
        TableRef.current.resetPagination();
      }
    } else if (bulkHandleAPIIssueRes.error) {
      toast.error(bulkHandleAPIIssueRes.error.message);
    }
  }, [ bulkHandleAPIIssueRes.data, bulkHandleAPIIssueRes.error ]);

  /**
   * Function to define bulk order actions
   */
  const bulkActionOptions = React.useCallback((bulkSelectData: BulkSelectState) => {
    return [
      {
        title: "Retry API import",
        action: () => bulkHandleAPIIssueReq({
          action: "retry",
          processJobIds: _.values(bulkSelectData)
        })
      },
      {
        title: "Ignore API issue",
        action: () => bulkHandleAPIIssueReq({
          action: "ignore",
          processJobIds: _.values(bulkSelectData)
        })
      }
    ];
  }, [ bulkHandleAPIIssueReq ]);

  /**
   * Fetches table data
   */

  const onFetchData = React.useCallback((filters: FilterParams<IProcessJob>, pagination: PaginatedParams<IProcessJob>) => {
    // Fetch support tickets
    listOrdersWithIssuesReq({
      filters,
      pagination
    });
  }, [ listOrdersWithIssuesReq ]);

  // listSupportTicketsRes change
  React.useEffect(() => {
    // If an error occurs show a toast
    if (listOrdersWithIssuesRes.error) {
      toast.error(listOrdersWithIssuesRes.error.message);
    }
  }, [ listOrdersWithIssuesRes.error ]);

  return (
    <>
      <Table
        tableId="apiImportIssues"
        ref={TableRef}
        loading={listOrdersWithIssuesRes.pending || retryAPIJobRes.pending}
        tableOf={`${_.capitalize(globalSettings?.erp.name)} Import Issues`}
        view="list"
        defaultPagination={defaultPagination}
        data={listOrdersWithIssuesRes.data ? listOrdersWithIssuesRes.data.result : []}
        onFetchData={onFetchData}
        pages={listOrdersWithIssuesRes.data ? listOrdersWithIssuesRes.data.pages : 0}
        totalRows={listOrdersWithIssuesRes.data ? listOrdersWithIssuesRes.data.count : 0}
        bulkActionOptions={bulkSelectData => bulkActionOptions(bulkSelectData)}
        bulkActionsLoading={bulkHandleAPIIssueRes.pending}
        columns={connectorOrderImportIssueTableColumns({
          ActionButton: (row: any) => {
            const actions = tableActions(row);

            return actions.length ? (
              <ActionButton
                title="actions"
                items={tableActions(row)}
              />
            ) : null;
          }
        })}
      />
    </>
  );
};

export default APIOrderImportTab;
